package co.uk.sacursoftware.ssinventory.util

import kotlinx.datetime.*

expect fun getCurrentLocalDate(): LocalDate
expect fun getCurrentEpochMillis(): Long

object DateTimeUtils {
    
    fun getCurrentDate(): LocalDate = getCurrentLocalDate()
    fun getCurrentMillis(): Long = getCurrentEpochMillis()

    fun getValidReportWeeks(count: Int = 12): List<Pair<LocalDate, LocalDate>> {
        val weeks = mutableListOf<Pair<LocalDate, LocalDate>>()
        val today = getCurrentLocalDate()
        
        // Move back to the most recent Saturday
        var currentSaturday = today
        while (currentSaturday.dayOfWeek != DayOfWeek.SATURDAY) {
            currentSaturday = currentSaturday.minus(1, DateTimeUnit.DAY)
        }
        
        repeat(count) {
            val startSunday = currentSaturday.minus(6, DateTimeUnit.DAY)
            weeks.add(startSunday to currentSaturday)
            // Move to previous Saturday
            currentSaturday = currentSaturday.minus(7, DateTimeUnit.DAY)
        }
        
        return weeks
    }

    fun formatDate(date: LocalDate): String {
        return "${date.year}-${date.month.number.toString().padStart(2, '0')}-${date.dayOfMonth.toString().padStart(2, '0')}"
    }

    fun formatDisplayDate(date: LocalDate): String {
        val yearShort = date.year.toString().takeLast(2)
        val monthStr = date.month.number.toString().padStart(2, '0')
        val dayStr = date.dayOfMonth.toString().padStart(2, '0')
        return "$dayStr/$monthStr/$yearShort"
    }

    fun formatIsoDisplayDate(isoString: String?): String {
        if (isoString == null) return ""
        return try {
            val date = LocalDate.parse(isoString)
            formatDisplayDate(date)
        } catch (_: Exception) {
            isoString
        }
    }

    fun formatCountTimestamp(timestampStr: String?): String {
        if (timestampStr.isNullOrBlank()) return ""
        return try {
            val parts = timestampStr.trim().split(" ", "T")
            val datePart = parts.getOrNull(0) ?: return timestampStr
            val timePart = (parts.getOrNull(1) ?: "00:00")
                .substringBefore("Z")
                .substringBefore("+")
                .substringBefore(".")

            val date = LocalDate.parse(datePart)
            val timeComps = timePart.split(":")
            val hourInt = timeComps.getOrNull(0)?.toIntOrNull() ?: 0
            val minStr = (timeComps.getOrNull(1) ?: "00").padStart(2, '0')

            // Stored timestamps are 1 hour ahead of the intended display time.
            // Roll the calendar date back when crossing midnight so month/year
            // boundaries stay valid (the old day-1 logic could show day 00).
            var adjustedHour = hourInt - 1
            var adjustedDate = date
            if (adjustedHour < 0) {
                adjustedHour += 24
                adjustedDate = date.minus(1, DateTimeUnit.DAY)
            }

            val yearShort = adjustedDate.year.toString().takeLast(2)
            val monthStr = adjustedDate.month.number.toString().padStart(2, '0')
            val dayStr = adjustedDate.dayOfMonth.toString().padStart(2, '0')
            val hourStr = adjustedHour.toString().padStart(2, '0')

            "$dayStr/$monthStr/$yearShort $hourStr:$minStr"
        } catch (_: Exception) {
            timestampStr
        }
    }

    fun formatTitleDate(timestampStr: String?): String {
        if (timestampStr.isNullOrBlank()) return ""
        val formatted = formatCountTimestamp(timestampStr)
        return formatted.substringBefore(" ").ifBlank { formatted }
    }

    fun getRecentSundays(count: Int = 12): List<LocalDate> {
        val sundays = mutableListOf<LocalDate>()
        val today = getCurrentLocalDate()
        
        // Find most recent Sunday
        var currentSunday = today
        while (currentSunday.dayOfWeek != DayOfWeek.SUNDAY) {
            currentSunday = currentSunday.minus(1, DateTimeUnit.DAY)
        }
        
        repeat(count) {
            sundays.add(currentSunday)
            currentSunday = currentSunday.minus(7, DateTimeUnit.DAY)
        }
        return sundays
    }

    fun getRecentSaturdays(count: Int = 12): List<LocalDate> {
        val saturdays = mutableListOf<LocalDate>()
        val today = getCurrentLocalDate()
        
        // Find most recent Saturday
        var currentSaturday = today
        while (currentSaturday.dayOfWeek != DayOfWeek.SATURDAY) {
            currentSaturday = currentSaturday.minus(1, DateTimeUnit.DAY)
        }
        
        repeat(count) {
            saturdays.add(currentSaturday)
            currentSaturday = currentSaturday.minus(7, DateTimeUnit.DAY)
        }
        return saturdays
    }

    fun calculateWeeksBetween(startDateStr: String, endDateStr: String): Double {
        return try {
            val start = LocalDate.parse(startDateStr)
            val end = LocalDate.parse(endDateStr)
            val days = start.daysUntil(end) + 1 // Inclusive
            days / 7.0
        } catch (_: Exception) {
            1.0
        }
    }

    fun formatDailyDate(dateStr: String): String {
        return try {
            val date = LocalDate.parse(dateStr)
            val dayOfWeek = date.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }.take(3)
            val day = date.dayOfMonth.toString().padStart(2, '0')
            val month = date.month.number.toString().padStart(2, '0')
            val year = date.year.toString().takeLast(2)
            "$dayOfWeek $day/$month/$year"
        } catch (_: Exception) {
            dateStr
        }
    }
}
